import { Octokit } from '@octokit/rest';
import { FileContent } from '../types';

interface CommitFileOptions {
  repo: string;
  owner: string;
  path: string;
  content: string;
  message: string;
  branch?: string;
  token: string;
}

// Initialize Octokit with token
const getOctokit = (token: string) => {
  return new Octokit({ auth: token });
};

// Commit a file to a repository
export const commitFile = async ({
  repo,
  owner,
  path,
  content,
  message,
  branch,
  token
}: CommitFileOptions) => {
  const octokit = getOctokit(token);
  
  try {
    // Check if file exists
    let sha: string | undefined;
    
    try {
      const { data } = await octokit.repos.getContent({
        owner,
        repo,
        path,
        ref: branch,
      });
      
      if ('sha' in data) {
        sha = data.sha;
      } else if (Array.isArray(data) && data.length > 0 && 'sha' in data[0]) {
        sha = data[0].sha;
      }
    } catch (error) {
      // File doesn't exist, which is fine for creating new files
      console.log('File doesn\'t exist yet, creating new file');
    }
    
    // Convert content to base64
    const contentBase64 = btoa(unescape(encodeURIComponent(content)));
    
    // Commit the file
    const response = await octokit.repos.createOrUpdateFileContents({
      owner,
      repo,
      path,
      message,
      content: contentBase64,
      sha,
      branch: branch || 'main',
    });
    
    return response.data;
  } catch (error) {
    console.error('Error committing file:', error);
    throw error;
  }
};

// Get repositories for the authenticated user
export const getUserRepositories = async (token: string) => {
  const octokit = getOctokit(token);
  
  try {
    const { data } = await octokit.repos.listForAuthenticatedUser({
      sort: 'updated',
      per_page: 100,
    });
    
    return data;
  } catch (error) {
    console.error('Error fetching repositories:', error);
    throw error;
  }
};

// Generate authorization URL for GitHub OAuth
export const getAuthUrl = (clientId: string, redirectUri: string) => {
  const scope = 'repo user';
  return `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;
}; 